import {Button, Input, Link} from "@heroui/react";
import {useMutation, useQuery} from '@tanstack/react-query';
import repositories from '../../repositories.jsx';
import {useForm} from '@mantine/form';
import toast from 'react-hot-toast';
import {useNavigate, useParams} from 'react-router-dom';
import {useState} from 'react';

const inputClassNames = {
	base: 'group',
	label: 'text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400 group-data-[focus=true]:text-primary group-data-[filled-within=true]:text-primary',
	inputWrapper: 'h-[58px] rounded-[14px] bg-[#f8f7ff] border-[1.5px] border-[#e0d9f7] shadow-none ' +
		'data-[hover=true]:bg-[#f8f7ff] ' +
		'group-data-[focus=true]:!bg-white group-data-[focus=true]:!border-primary ' +
		'group-data-[focus=true]:shadow-[0_0_0_3px_rgba(90,10,224,0.10)] transition-all',
	input: 'text-sm text-slate-900',
};

export default function ResetPassword() {
	const {token} = useParams();
	const navigate = useNavigate();
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);

	const form = useForm({
		initialValues: {
			token: '',
			password: '',
			password_confirmation: '',
		},
	});
	const onSubmit = useMutation({
		mutationFn: () => repositories.auth.resetPassword(form.values),
		onSuccess: () => {
			toast.success('Password successfully changed, please log in');
			navigate('/login');
		},
		onError: (error) => {
			toast.error(error?.response?.data?.detail || 'Something went wrong');
		},
	});

	const verifyToken = useQuery({
		queryFn: () => repositories.auth.verifyResetToken(token).then(res => {
			form.setFieldValue('token', token);
			return res;
		}),
	});

	const submit = () => {
		localStorage.removeItem('token');
		onSubmit.mutate();
	};
	const onEnter = (e) => {
		if (e.key === 'Enter') submit();
	};

	const eyeToggle = (
		<button
			type="button"
			aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
			onClick={() => setIsPasswordVisible(!isPasswordVisible)}
			className="text-slate-400 hover:text-slate-600 transition-colors">
			<i className={`eva ${isPasswordVisible ? 'eva-eye-outline' : 'eva-eye-off-2-outline'} text-xl`}/>
		</button>
	);

	const pwd = form.values.password || '';
	const confirm = form.values.password_confirmation || '';
	const strength = pwd.length === 0 ? 0 : pwd.length < 6 ? 1 : pwd.length < 8 ? 2 : pwd.length < 10 ? 3 : 4;
	const strengthColor = strength <= 1 ? '#f87171' : strength <= 2 ? '#fbbf24' : '#4ade80';
	const mismatch = confirm.length > 0 && confirm !== pwd;

	// ── Loading: verifying the token ──
	if (verifyToken.isLoading) {
		return (
			<div className="flex flex-col items-center justify-center py-16 gap-4">
				<span className="w-9 h-9 rounded-full border-[3px] border-primary/20 border-t-primary animate-spin"/>
				<p className="text-sm text-slate-400">Verifying your reset link…</p>
			</div>
		);
	}

	// ── Invalid / expired token ──
	if (verifyToken.isError) {
		return (
			<div className="text-center py-4">
				<div className="auth-pop-in w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 text-rose-500 bg-rose-50 border border-rose-100">
					<svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
						<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/>
					</svg>
				</div>
				<h2 className="text-2xl font-black text-slate-900 mb-2">Link expired or invalid.</h2>
				<p className="text-sm text-slate-500 mb-8">
					{verifyToken.error?.response?.data?.detail || 'This reset link is no longer valid. Request a fresh one to continue.'}
				</p>
				<Button
					onPress={() => navigate('/forgot-password')}
					disableRipple
					className="auth-btn-wrap w-full h-auto py-3.5 mb-4 rounded-2xl text-white text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
					style={{background: 'linear-gradient(to right, #5A0AE0, #8C2CE2)', boxShadow: '0 6px 20px rgba(90,10,224,0.3)'}}>
					<span className="auth-shimmer"/>
					Request a new link
				</Button>
				<p className="text-center text-xs text-slate-400">
					Remember your password?{' '}
					<Link href="/login" className="text-primary hover:underline font-semibold cursor-pointer">Sign in</Link>
				</p>
			</div>
		);
	}

	// ── Set a new password ──
	return (
		<>
			<button onClick={() => navigate('/login')}
			        className="auth-fade-1 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 font-medium mb-8 transition-colors">
				<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
					<path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
				</svg>
				Back to sign in
			</button>

			<div className="auth-fade-1 w-12 h-12 rounded-2xl flex items-center justify-center mb-6"
			     style={{background: 'linear-gradient(135deg, rgba(90,10,224,0.12), rgba(140,44,226,0.12))', border: '1.5px solid rgba(90,10,224,0.2)'}}>
				<svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
					<path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
				</svg>
			</div>

			<h1 className="auth-fade-1 font-sans text-[32px] font-black text-slate-900 leading-tight tracking-tight mb-1">
				Set a new password.
			</h1>
			<p className="auth-fade-1 text-sm text-slate-500 mb-8">
				Choose a strong password to secure your account.
			</p>

			{/* new password */}
			<div className="auth-fade-2 mb-2">
				<Input
					label="New password"
					labelPlacement="inside"
					variant="flat"
					autoComplete="new-password"
					onKeyDown={onEnter}
					{...form.getInputProps('password')}
					type={isPasswordVisible ? 'text' : 'password'}
					endContent={eyeToggle}
					classNames={inputClassNames}
				/>
			</div>

			{/* strength */}
			<div className="auth-fade-2 flex gap-1 mb-5">
				{[0, 1, 2, 3].map((i) => (
					<div key={i} className="h-1 flex-1 rounded-full transition-colors duration-300"
					     style={{background: i < strength ? strengthColor : '#e2e8f0'}}/>
				))}
			</div>

			{/* confirm password */}
			<div className="auth-fade-3 mb-2">
				<Input
					label="Confirm password"
					labelPlacement="inside"
					variant="flat"
					autoComplete="new-password"
					onKeyDown={onEnter}
					{...form.getInputProps('password_confirmation')}
					type={isPasswordVisible ? 'text' : 'password'}
					endContent={eyeToggle}
					classNames={inputClassNames}
				/>
			</div>
			<p className={`auth-fade-3 text-[12px] mb-6 transition-opacity ${mismatch ? 'text-rose-500 opacity-100' : 'opacity-0'}`}>
				Passwords don&apos;t match
			</p>

			{/* submit */}
			<Button
				isLoading={onSubmit.isPending}
				isDisabled={mismatch || !pwd || !confirm}
				disableRipple
				onPress={submit}
				className="auth-fade-3 auth-btn-wrap w-full h-auto py-3.5 mb-5 rounded-2xl text-white text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98] data-[disabled=true]:opacity-60 data-[disabled=true]:hover:scale-100"
				style={{background: 'linear-gradient(to right, #5A0AE0, #8C2CE2)', boxShadow: '0 6px 20px rgba(90,10,224,0.3)'}}
				endContent={!onSubmit.isPending && <i className="eva eva-checkmark-outline text-xl"/>}>
				<span className="auth-shimmer"/>
				Change password
			</Button>

			<p className="auth-fade-4 text-center text-xs text-slate-400">
				Remember your password?{' '}
				<Link href="/login" className="text-primary hover:underline font-semibold cursor-pointer">Sign in</Link>
			</p>
		</>
	);
}
